import { useNavigate } from 'react-router-dom';

import { agentRoleLabel, useGetAgentsQuery } from '../../store/agents-api.js';
import { messageOf } from '../../store/error-message.js';
import { PlanetSpinner } from '../../ui/BrandLogo.js';
import { initials } from '../../ui/initials.js';
import { when } from '../../ui/when.js';
import styles from './AgentsPage.module.css';

/**
 * Agent configuration (S10).
 *
 * ## Why this list has two rows and not seven
 *
 * The platform project holds seven agents. Two of them do the work this console
 * exists for: one conducts the calls, one classifies them afterwards. The others
 * belong to other work, and offering them here would invite an operator to change
 * something with no visible effect on this console and an unknown effect
 * elsewhere. So the server returns the two it is configured with.
 *
 * The mock's search box is gone with them: a filter over two rows is a control
 * that can only ever hide something the operator came here to find.
 *
 * ## Why the editor is a route and not a panel
 *
 * Instructions run to five thousand characters. The interaction drawer works
 * because a summary and a transcript are read together; a prompt is *written*,
 * with the line numbers and the width of an editor. Making it a route rather than
 * state held here is what lets the sidebar link lead back out of it.
 */
export function AgentsPage(): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading, isError, error, refetch } = useGetAgentsQuery();

  const agents = data?.items ?? [];

  return (
    <section className={styles.view}>
      {isLoading && (
        <div className={styles.state}>
          <span className={styles.loading}>
            <PlanetSpinner maskId="ps-mark-agents" />
            Loading agents…
          </span>
        </div>
      )}

      {isError && (
        <div className={styles.state}>
          <p>{messageOf(error)}</p>
          <button type="button" className={styles.retry} onClick={() => void refetch()}>
            Try again
          </button>
        </div>
      )}

      {!isLoading &&
        !isError &&
        agents.map((agent) => (
          <div
            key={agent.id}
            className={styles.card}
            role="button"
            tabIndex={0}
            aria-label={`Configure ${agent.presetName ?? agent.name}`}
            onClick={() => void navigate(`/agents/${agent.id}`)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                void navigate(`/agents/${agent.id}`);
              }
            }}
          >
            <div className={styles.av} aria-hidden="true">
              {initials(agent.presetName ?? agent.name)}
            </div>

            <div className={styles.main}>
              <div className={styles.titleRow}>
                <b>{agent.presetName ?? agent.name}</b>
                <span className={styles.role}>{agentRoleLabel(agent)}</span>
              </div>

              {agent.description !== null && <p className={styles.desc}>{agent.description}</p>}

              <div className={styles.meta}>
                {/* The platform's own timestamp, not ours: what an operator wants
                    to know is when the agent last changed, wherever it was
                    changed from. */}
                <span>
                  Updated {agent.sirmaUpdatedAt === null ? 'never' : when(agent.sirmaUpdatedAt)}
                  {agent.lastUpdatedBy !== null && ` by ${agent.lastUpdatedBy}`}
                </span>
                {agent.role === 'OUTBOUND' && (
                  <>
                    <span className={styles.dot} aria-hidden="true">
                      ·
                    </span>
                    <span>Speaks as {agent.name}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}

      {!isLoading && !isError && agents.length === 0 && (
        <div className={styles.empty}>
          <h3>No agents configured</h3>
          {/* Deliberately specific: an empty list here almost always means a
              missing id or an unreachable platform, not an empty project. */}
          <p>
            The console reads the two agents named in its configuration. If this is empty, either
            those ids are unset or the platform could not be reached.
          </p>
        </div>
      )}
    </section>
  );
}
